import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  cloneElement,
  useRef,
  useContext,
  useImperativeHandle,
  useLayoutEffect,
} from 'react';

import { hasVisibleVirtualKeyboard } from '@editor/utils/keyboard/hasVisibleVirtualKeyboard';
import { createPortal } from 'react-dom';

import Context from '../context';
import PanelHeader from '../utilities/PanelHeader/PanelHeader';

import {
  PopoverWrapper,
  Content,
  PopoverTip,
  PopoverContainer,
  HeadWrapper,
  PointerLine,
} from './styles';
import { defaultTheme } from './theme';
import { PopoverAlign, PopoverPlacement, PopoverProps, Rect } from './types';

const isPopoverFriendly = (node: Element | null): boolean => {
  while (node) {
    if (node.getAttribute) {
      const classes = node.getAttribute('class');
      if (classes && classes.split(' ').includes('popover-friend')) {
        return true;
      }
    }

    node = node.parentElement;
  }

  return false;
};

/**
 * We need to make sure that the element is in DOM. Otherwise we cannot calculate its coordinates properly.
 * See https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
 * Note! It may not work properly in the following cases:
 * 1. the element or any ancestor has the display property set to none
 * 2. the element is <body> or <html>
 */
const isElementInPlace = (element: HTMLElement): boolean =>
  Boolean(element && (element.offsetParent || element.getClientRects().length));

/**
 * The popover component is used to display content that is floating above the default layer.
 * It can be triggered by a target component, that is the only child of it.
 */
const Popover: React.FC<React.PropsWithChildren<PopoverProps>> = ({
  open,
  placement = PopoverPlacement.Bottom,
  label,
  padding = 5,
  margin = '0px',
  align = PopoverAlign.Center,
  marginForTip,
  content,
  dataTestId,
  onMouseLeave,
  onClose,
  onWillClose,
  showHeader = true,
  width = '290px',
  hideTip,
  ignoreEventOutside,
  theme: _theme,
  useHover,
  onClick,
  onTouchStart,
  onClickOutside,
  className,
  customHeaderElement,
  wrapperMargin = 0,
  updatePositionOnContentSizeChange,
  forceUpdatePositionKey,
  hideCloseIcon,
  closeIconType,
  positionRef,
  openDelay,
  closeDelay,
  showPointerLine,
  arrowTipPositionOverride,
  ...props
}) => {
  const context = useContext(Context);
  const theme = useMemo(() => ({ ...defaultTheme, ..._theme }), [_theme]);

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    left: '0',
    top: '0',
    arrowPos: '0',
    isInPosition: false,
  });

  const [hoverTarget, setHoverTarget] = useState(false);
  const [hoverPopover, setHoverPopover] = useState(false);

  const targetRef = useRef<HTMLElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // make the targetRef available via the ref prop
  useImperativeHandle(props.targetRef, () => ({
    current: targetRef?.current,
  }));

  const close = useCallback(() => {
    onWillClose && onWillClose();
    if (open) {
      return;
    } // if popover is forced to stay open, don't close

    onClose && onClose();
    setIsOpen(false);
  }, [onClose, onWillClose, open]);

  const handleEventOutside = useCallback(
    (event: PointerEvent | WheelEvent) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      /**
       * One more HACK: if one popover opens another popover, that opens another popover, etc. these popovers
       * probably have the `popover-friend` class. We want to close some popovers by clicking on others, but not all.
       * E.g. we have 1P -> 2P -> 3P -> 4P. If we click on 2, we want to close 3 and 4 but not 1 and 2.
       * TODO we should consider some type of global popover manager to handle this case.
       */
      const isPositionRefFriendly = positionRef?.current
        ? isPopoverFriendly(positionRef.current)
        : false;

      if (isPopoverFriendly(event.target) && !isPositionRefFriendly) {
        return;
      }

      if (ignoreEventOutside) {
        return;
      }

      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        targetRef.current &&
        !targetRef.current.contains(event.target) &&
        document.contains(event.target) // an element might re-render when its clicked, so we check if it still exists
      ) {
        setHoverPopover(false);
        setHoverTarget(false);
        onClickOutside && onClickOutside();
        close();
      }
    },
    [close, ignoreEventOutside, onClickOutside, positionRef]
  );

  useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(
    props.wrapperRef,
    () => popoverRef.current
  );

  const getContainer = useCallback(() => {
    return props.container?.current || context.getRootContainer();
  }, [props.container, context]);

  const calculatePosition = useCallback(
    (targetRect: Rect, popoverRect: Rect, containerRect: Rect) => {
      let left = 0;
      let top = 0;
      let arrowPos = 0;

      const arrowWidth = hideTip ? 0 : 10;
      const verticalArrowPadding = hideTip ? 0 : 20;
      const horizontalArrowPadding = hideTip ? 0 : 12;

      const placementIsVertical = placement === 'top' || placement === 'bottom';

      if (placementIsVertical) {
        if (placement === 'top') {
          top = targetRect.top - popoverRect.height - padding;
        } else {
          top = targetRect.bottom + padding;
        }

        if (align === 'center') {
          left = targetRect.left + targetRect.width / 2 - popoverRect.width / 2;
          arrowPos = popoverRect.width / 2 - arrowWidth / 2;
        } else if (align === 'start') {
          left = targetRect.left - horizontalArrowPadding;
          arrowPos =
            targetRect.width / 2 + arrowWidth / 2 + horizontalArrowPadding;
        } else {
          left =
            targetRect.left +
            targetRect.width -
            popoverRect.width +
            horizontalArrowPadding;
          arrowPos =
            popoverRect.width -
            targetRect.width / 2 -
            arrowWidth / 2 -
            horizontalArrowPadding;
        }
      } else {
        if (placement === 'right') {
          left = targetRect.right + padding;
        } else {
          left = targetRect.left - popoverRect.width - padding;
        }

        if (align === 'center') {
          top = targetRect.top + targetRect.height / 2 - popoverRect.height / 2;
          arrowPos = popoverRect.height / 2 - arrowWidth / 2;
        } else if (align === 'start') {
          top = targetRect.top - verticalArrowPadding;
          arrowPos =
            targetRect.height / 2 - arrowWidth / 2 + verticalArrowPadding;
        } else {
          top =
            targetRect.top +
            targetRect.height -
            popoverRect.height +
            verticalArrowPadding;
          arrowPos =
            popoverRect.height -
            targetRect.height / 2 -
            arrowWidth / 2 -
            verticalArrowPadding;
        }
      }

      top = top - containerRect.top;
      left = left - containerRect.left;

      // Keep popovers within window
      if (left > containerRect.width - popoverRect.width - wrapperMargin) {
        if (placementIsVertical) {
          arrowPos =
            arrowPos +
            (left - (containerRect.width - popoverRect.width - wrapperMargin));
        }
        left = containerRect.width - popoverRect.width - wrapperMargin;
      } else if (left < 0 + wrapperMargin) {
        if (placementIsVertical) {
          arrowPos = arrowPos + left + wrapperMargin;
        }
        left = 0 + wrapperMargin;
      }

      // Don't adjust top property if the virtual keyboard is on screen:
      const isFullscreen = document.body.dataset.isFullscreen === 'true';
      if (isFullscreen && hasVisibleVirtualKeyboard()) {
        return [left.toFixed(), top.toFixed(), arrowPos.toFixed()];
      }

      if (top > containerRect.height - popoverRect.height - wrapperMargin) {
        if (!placementIsVertical) {
          arrowPos =
            arrowPos +
            (top - (containerRect.height - popoverRect.height - wrapperMargin));
        }
        top = containerRect.height - popoverRect.height - wrapperMargin;
      }

      if (top < 0 + wrapperMargin) {
        if (!placementIsVertical) {
          arrowPos = arrowPos + top + wrapperMargin;
        }
        top = 0 + wrapperMargin;
      }

      return [left.toFixed(), top.toFixed(), arrowPos.toFixed()];
    },
    [align, padding, placement, hideTip, wrapperMargin]
  );

  const updatePosition = useCallback(() => {
    const calcPositionTarget = positionRef
      ? positionRef.current
      : targetRef.current;

    if (!isOpen || !calcPositionTarget) {
      return;
    }

    const container = getContainer();

    if (
      isElementInPlace(calcPositionTarget) &&
      popoverRef.current &&
      container
    ) {
      const targetRect = calcPositionTarget.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const calculatedPosition = calculatePosition(
        targetRect,
        popoverRect,
        containerRect
      );

      let [left] = calculatedPosition;
      const [, top, arrowPos] = calculatedPosition;

      if (width === '100%') {
        left = '0';
      }

      if (
        !position.isInPosition ||
        left !== position.left ||
        top !== position.top ||
        arrowPos !== position.arrowPos
      ) {
        setPosition({
          left,
          top,
          arrowPos,
          isInPosition: true,
        });
      }
    }
  }, [position, calculatePosition, getContainer, isOpen, width, positionRef]);

  // Update popover position when forceUpdatePositionKey changes
  useLayoutEffect(() => {
    updatePosition();
  }, [forceUpdatePositionKey, updatePosition]);

  const popoverWrapperRef = popoverRef?.current;
  useLayoutEffect(() => {
    // Update position on re-render
    updatePosition();

    const popupResizeObserver = new ResizeObserver(updatePosition);
    const shouldUpdatePositionOnContentSizeChange =
      updatePositionOnContentSizeChange && popoverWrapperRef;

    // Bind the event listener
    if (isOpen) {
      document.addEventListener('pointerdown', handleEventOutside);
      document.addEventListener('wheel', handleEventOutside);
      window.addEventListener('resize', updatePosition);

      shouldUpdatePositionOnContentSizeChange &&
        popupResizeObserver.observe(popoverWrapperRef);
    }
    return (): void => {
      // Unbind the event listener on clean up
      document.removeEventListener('pointerdown', handleEventOutside);
      document.removeEventListener('wheel', handleEventOutside);
      window.removeEventListener('resize', updatePosition);
      shouldUpdatePositionOnContentSizeChange &&
        popupResizeObserver.unobserve(popoverWrapperRef);
    };
  }, [
    handleEventOutside,
    updatePosition,
    isOpen,
    popoverWrapperRef,
    updatePositionOnContentSizeChange,
  ]);

  const popoverElement = useMemo(
    () => (
      <PopoverContainer
        className={className}
        data-popover-classname={className ?? ''}
        theme={theme}
      >
        <PopoverWrapper
          ref={popoverRef}
          isInPosition={isOpen && position.isInPosition}
          margin={margin}
          placement={placement}
          showPointerLine={showPointerLine}
          style={{
            left: position.left + 'px',
            top: position.top + 'px',
          }}
          theme={theme}
          width={width}
          onMouseEnter={(): void => setHoverPopover(true)}
          onMouseLeave={(): void => {
            setHoverPopover(false);
            onMouseLeave && onMouseLeave();
          }}
        >
          {!hideTip && (
            <PopoverTip
              margin={marginForTip || margin}
              placement={placement}
              style={
                placement === 'top' || placement === 'bottom'
                  ? {
                      left:
                        arrowTipPositionOverride ?? position.arrowPos + 'px',
                    }
                  : {
                      top: arrowTipPositionOverride ?? position.arrowPos + 'px',
                    }
              }
              theme={theme}
            />
          )}

          {showPointerLine && hideTip && (
            <PointerLine
              // Space between child and popover is the height of line
              height={padding}
              placement={placement}
              theme={theme}
            />
          )}
          <Content data-testid={dataTestId} theme={theme}>
            {showHeader && (
              <HeadWrapper theme={theme}>
                <PanelHeader
                  closeIconType={closeIconType}
                  customElement={customHeaderElement}
                  hideCloseIcon={hideCloseIcon}
                  iconHeight={theme.iconHeight}
                  label={label}
                  theme={{ ...theme, padding: '0px' }}
                  onClose={close}
                />
              </HeadWrapper>
            )}

            {content}
          </Content>
        </PopoverWrapper>
      </PopoverContainer>
    ),
    [
      className,
      isOpen,
      position.isInPosition,
      position.left,
      position.top,
      position.arrowPos,
      margin,
      placement,
      theme,
      width,
      hideTip,
      marginForTip,
      arrowTipPositionOverride,
      showPointerLine,
      padding,
      dataTestId,
      showHeader,
      closeIconType,
      customHeaderElement,
      hideCloseIcon,
      label,
      close,
      content,
      onMouseLeave,
    ]
  );

  const renderPopover = useCallback(() => {
    const container = getContainer();
    if (container && isOpen) {
      return createPortal(popoverElement, container);
    }
    return null;
  }, [isOpen, getContainer, popoverElement]);

  useImperativeHandle(props.toggleRef, () => ({ toggle }));

  const handleOnClick = (event: React.PointerEvent<Element>): void => {
    event.preventDefault();
    if (!useHover) {
      toggle(event);
    }

    // only trigger onClick on desktops when a mouse is used
    if (event.nativeEvent?.pointerType === 'mouse') {
      onClick && onClick(event);
    }

    const nativeEvent = event.nativeEvent;
    if ('mozInputSource' in nativeEvent) {
      if (
        [0, 1, 4].includes(
          (nativeEvent as { mozInputSource: number }).mozInputSource
        )
      ) {
        // firefox source is mouse or unknown
        onClick && onClick(event);
      }
    }
  };

  const handleOnTouchStart = (): void => {
    if (onTouchStart) {
      onTouchStart();
    }
    setHoverTarget((prev) => !prev);
  };

  const openPopover = useCallback(() => {
    setIsOpen(true); // open
    props.onOpened && props.onOpened();

    // reset for animation
    setPosition((prev) => ({ ...prev, isInPosition: false }));
    renderPopover();
    updatePosition();
  }, [props, renderPopover, updatePosition]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (!useHover) {
      return;
    }

    const isHover = hoverTarget || hoverPopover;

    const handleOpenCloseWithDelay = (): void => {
      if (!isOpen && isHover) {
        timeoutId = setTimeout(() => {
          openPopover();
        }, openDelay || 0);
      } else if (isOpen && !isHover) {
        timeoutId = setTimeout(() => {
          close();
        }, closeDelay || 0);
      }
    };

    if (openDelay || closeDelay) {
      handleOpenCloseWithDelay();
    } else {
      if (!isOpen && isHover) {
        openPopover();
      } else if (isOpen && !isHover) {
        close();
      }
    }

    return (): void => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    isOpen,
    useHover,
    hoverTarget,
    hoverPopover,
    openPopover,
    close,
    openDelay,
    closeDelay,
  ]);

  const toggle = (evt?: React.PointerEvent<Element>): void => {
    evt && evt.stopPropagation();
    props.onToggle && props.onToggle();
    if (isOpen) {
      close();
      return;
    }

    openPopover();
  };

  useEffect(() => {
    if (open === undefined || isOpen === open) {
      return;
    }

    if (!open) {
      close();
      return;
    }
    setTimeout(() => {
      setIsOpen(true); // open
      // reset for animation
      setPosition((prev) => ({ ...prev, isInPosition: false }));
      renderPopover();
      updatePosition();
    }, openDelay || 0);
  }, [open, close, updatePosition, renderPopover, isOpen, openDelay]);

  if (!props.children) {
    return null;
  }

  if (
    typeof props.children === 'string' ||
    typeof props.children === 'number' ||
    typeof props.children === 'boolean' ||
    typeof props.children === 'bigint' ||
    !('type' in props.children) // Check for react element. Iterables can't be cloned
  ) {
    return null;
  }

  return (
    <>
      {cloneElement(props.children, {
        onClick: handleOnClick,
        onMouseEnter: () => setHoverTarget(true),
        onMouseLeave: () => setHoverTarget(false),
        onTouchStart: handleOnTouchStart,
        isActive: isOpen,
        ref: targetRef,
      })}
      {renderPopover()}
    </>
  );
};

export default Popover;
